export interface CallResult {
  API: string[];
  args: any[];
}

export type OnRequestHandler<T extends any[]> = (...args: T) => any;

export type CommonCallingFunction<T extends any[]> = {
  /**
   * for Client
   * @param args
   * @returns
   */
  call: (...args: T) => CallResult; // Explicitly define the 'call' signature
  /**
   * for Server
   * @param args
   * @returns
   */
  onRequest: (handler: OnRequestHandler<T>) => any; // Explicitly define the 'call' signature
  /**
   * for Client listen to Server events
   * @param eventName
   * @param data
   * @returns
   */
  on: (
    eventName: string,
    handler: (data: any) => void | Promise<void>
  ) => void | Promise<void>;
  /**
   * for Server, send event data to Client
   * @param eventName
   * @param data
   * @returns
   */
  sendEvent: (eventName: string, data: any) => void | Promise<void>;
};

// Define a type that allows any string key dynamically,
// but specifically types the 'call' method.
export type DynamicChainedProxy = {
  [key: string]: any; // Allow dynamic property access returning any type
} & CommonCallingFunction<any[]>;

// Update the factory function signature and the final type assertion
export function createAPIProxy(
  customCallingFunction?: (
    api: string[],
    args: any[],
    callingFunctionName: "call" | "onRequest" | "on" | "sendEvent"
  ) => any
): DynamicChainedProxy {
  const createHandler = (path: string[]): ProxyHandler<any> => {
    return {
      get(target: any, propKey: string | symbol, receiver: any): any {
        if (typeof propKey === "string") {
          switch (propKey) {
            /**
             * Client Call API Function
             */
            case "call":
            case "onRequest":
            case "on":
            case "sendEvent":
              // Return the final function
              return (...args: any[]): CallResult => {
                if (
                  customCallingFunction &&
                  typeof customCallingFunction === "function"
                ) {
                  const callingResult = customCallingFunction(
                    path,
                    args,
                    propKey
                  );
                  if (callingResult) {
                    return callingResult;
                  }
                }
                return {
                  API: path,
                  args: args,
                };
              };
            default:
              // Chain the proxy
              const newPath = [...path, propKey];
              return new Proxy({}, createHandler(newPath));
          }
        }
        // Default behavior for symbols etc.
        return Reflect.get(target, propKey, receiver);
      },
    };
  };

  // Assert the final proxy conforms to our DynamicChainedProxy type
  return new Proxy({}, createHandler([])) as DynamicChainedProxy;
}

// --- Usage Examples (remain the same) ---

export const APIProxy = createAPIProxy();
